import React from "react";
import { Avatar, Chip, useTheme } from "@material-ui/core";
import { ChipProps } from "@material-ui/core/Chip";
import { Inbox, InboxArrowDown, InboxArrowUp } from "mdi-material-ui";
import { omit } from "@bonliva-crm/common";
import { GetInvoice, useGetInvoiceQuery } from "@graphql";
import { ThemeType } from "@theme";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { useInvoiceProvider } from "../useInvoiceProvider";

type InvoiceNumberChipRenderProps = {
  chip: React.ReactElement<any>;
  data: GetInvoice.Return["invoiceByNumber"];
  isError: boolean;
};

type Props = Omit<ChipProps, "children"> & {
  invoiceNumber: string;
  costCenterId: string;
  children: (renderProps: InvoiceNumberChipRenderProps) => React.ReactElement<any>;
};

export function InvoiceChip(props: Props) {
  const { invoiceNumber, children, ...rest } = props;
  const theme = useTheme<ThemeType>();
  const invoiceProvider = useInvoiceProvider(props.costCenterId);
  const { specialInvoices } = store().cache;
  const specialInvoiceNumbers = Object.keys(specialInvoices);

  const { data } = useGetInvoiceQuery({
    variables: {
      number: props.invoiceNumber,
      provider: invoiceProvider
    },
    fetchPolicy: "network-only"
  });

  if (specialInvoiceNumbers.includes(props.invoiceNumber)) {
    return (
      <Chip
        {...omit(rest, "onClick")}
        avatar={
          <Avatar style={{ backgroundColor: theme.palette.grey[400] }}>
            <Inbox fontSize="small" />
          </Avatar>
        }
        label={
          <span style={{ whiteSpace: "nowrap" }}>
            <span>{locale.bookingInvoiceType[specialInvoices[props.invoiceNumber]]}</span>
          </span>
        }
      />
    );
  }

  let avatar = (
    <Avatar style={{ backgroundColor: theme.palette.grey[400] }}>
      <Inbox fontSize="small" />
    </Avatar>
  );

  if (data && !data.invoiceByNumber) {
    avatar = (
      <Avatar
        style={{
          backgroundColor: theme.palette.grey[400],
          color: theme.palette.error.main
        }}
      >
        <Inbox fontSize="small" />
      </Avatar>
    );
  }

  if (data && data.invoiceByNumber && data.invoiceByNumber.IsCredit) {
    avatar = (
      <Avatar style={{ backgroundColor: theme.palette.error.main, color: "white" }}>
        <InboxArrowUp fontSize="small" />
      </Avatar>
    );
  }

  if (data && data.invoiceByNumber && !data.invoiceByNumber.IsCredit) {
    avatar = (
      <Avatar style={{ backgroundColor: theme.palette.success.main, color: "white" }}>
        <InboxArrowDown fontSize="small" />
      </Avatar>
    );
  }

  const isError = data && !data.invoiceByNumber;

  const labelDetails = [];

  // if (props.invoicedTotal) {
  //   labelDetails.push(`(${props.invoicedTotal} kr.)`);
  // }
  //
  // if (props.groupInvoicing === true) {
  //   labelDetails.push(locale.GroupInvoicing);
  // }

  const labelDetailsStr = labelDetails.join(", ");

  const label = (
    <div>
      <strong>#{props.invoiceNumber}</strong>
      {labelDetailsStr && <span style={{ marginLeft: 4 }}>{labelDetailsStr}</span>}
    </div>
  );

  const chip = (
    <Chip
      {...rest}
      style={{ color: isError ? theme.palette.error.main : undefined, cursor: "pointer" }}
      avatar={avatar}
      label={label}
    />
  );

  if (!children) {
    return chip;
  }

  return children({ chip, isError, data: data ? data.invoiceByNumber : null });
}
